/**
 * Secção "Mobilidade oficial" do PDF — lógica pura.
 *
 * Nunca faz chamadas: recebe apenas os grupos já resolvidos a partir da base
 * de dados curada e devolve as entradas a desenhar. Free/Premium só recebem
 * avisos essenciais + uma linha de teaser; Pro recebe os links oficiais.
 */
import { MODE_LABEL, type MobilityMode } from "./mobility/directory";
import type { MobilityGroup } from "./mobility/teaser";

export type PdfResourceEntry = { group: string; label: string; url: string; note?: string };

export type PdfMobilitySection = {
  title: string;
  note?: string;
  entries: PdfResourceEntry[];
  /** Linha curta mostrada a Free/Premium em vez dos links Pro. */
  teaserLine?: string;
};

export const PDF_MOBILITY_TITLE = "Mobilidade oficial";
export const PDF_MOBILITY_NOTE =
  "Precos, horarios e regras sao definidos pelos operadores oficiais e podem mudar sem aviso. A Routify nao vende bilhetes nem recebe comissao por estes links.";
export const PDF_MOBILITY_TEASER = "Links oficiais de mobilidade disponiveis no Routify Pro.";

/** Locais concretos já validados (levantamento de carro/bicicleta/trotinete). */
export type PdfPickupPoint = {
  city: string;
  mode: MobilityMode | string;
  name: string;
  mapsUrl: string;
  address?: string;
};

export function buildPdfMobilitySection(input: {
  tier: "pro" | "free";
  cities: Array<{ city: string; groups: MobilityGroup[] }>;
  pickupPoints?: PdfPickupPoint[];
}): PdfMobilitySection | null {
  const entries: PdfResourceEntry[] = [];
  let hasWarning = false;
  let hasOfficial = false;

  for (const { city, groups } of input.cities) {
    for (const group of groups) {
      const groupLabel = `${city} - ${MODE_LABEL[group.mode as MobilityMode] ?? group.label}`;
      // Avisos essenciais: em TODOS os planos.
      if (group.warning) {
        hasWarning = true;
        entries.push({ group: groupLabel, label: "Aviso importante", url: "", note: group.warning });
      }
      if (input.tier !== "pro") continue;
      for (const item of group.items) {
        if (!item.url) continue;
        hasOfficial = true;
        entries.push({
          group: groupLabel,
          label: `${item.providerName}${item.official ? " (fonte oficial)" : ""}`,
          url: item.url,
          ...(item.notes ? { note: item.notes } : {}),
        });
      }
    }
  }

  if (input.tier === "pro") {
    for (const p of input.pickupPoints ?? []) {
      if (!p.mapsUrl) continue;
      hasOfficial = true;
      entries.push({
        group: `${p.city} - ${MODE_LABEL[p.mode as MobilityMode] ?? String(p.mode)}`,
        label: p.name,
        url: p.mapsUrl,
        ...(p.address ? { note: p.address } : {}),
      });
    }
  }

  if (!hasWarning && !hasOfficial) return null;

  return {
    title: PDF_MOBILITY_TITLE,
    ...(hasOfficial ? { note: PDF_MOBILITY_NOTE } : {}),
    entries,
    ...(input.tier !== "pro" ? { teaserLine: PDF_MOBILITY_TEASER } : {}),
  };
}

/** Filtra grupos irrelevantes para o transporte escolhido (menos ruído no PDF). */
export function relevantGroupsForTransport(
  groups: MobilityGroup[],
  transport: string,
): MobilityGroup[] {
  const allowed: Record<string, string[]> = {
    car: ["parking", "tolls", "low_emission_zone", "car_rental", "transfer"],
    transit: ["transit_pass", "transit_tickets", "transfer", "ferry"],
    bike: ["bike_share", "scooter", "transit_pass", "low_emission_zone"],
    walk: ["transit_pass", "transit_tickets", "bike_share", "scooter", "transfer"],
  };
  const list = allowed[transport] ?? allowed.walk;
  return groups.filter((g) => list.includes(String(g.mode)));
}
